export interface PrintOptions {
  autoPrint?: boolean;
  autoClose?: boolean;
  onError?: (error: Error) => void;
}

// HD Quality Configuration
export const HD_QUALITY_CONFIG = {
  standard: {
    scale: 1.0,
    jpegQuality: 0.85,
    pngQuality: 0.9,
    imageTimeout: 2000
  },
  hd: {
    scale: 2.0,
    jpegQuality: 0.92,
    pngQuality: 0.95,
    imageTimeout: 3000
  },
  ultraHd: {
    scale: 2.5,
    jpegQuality: 0.95,
    pngQuality: 0.98,
    imageTimeout: 4000
  }
};

export const createPrintDocument = (content: string, title: string, styles: string): string => {
  return `
    <html>
      <head>
        <title>${title}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          ${styles}
          
          /* HD Quality Text Rendering */
          * {
            -webkit-font-smoothing: antialiased !important;
            -moz-osx-font-smoothing: grayscale !important;
            text-rendering: optimizeLegibility !important;
            font-smooth: always !important;
            -webkit-text-stroke: 0.01em transparent !important;
          }
          
          /* HD Quality Image Rendering */
          img {
            image-rendering: -webkit-optimize-contrast !important;
            image-rendering: crisp-edges !important;
            image-rendering: pixelated !important;
            max-width: 100% !important;
            height: auto !important;
          }
        </style>
      </head>
      <body>
        ${content}
      </body>
    </html>
  `;
};

// Enhanced logo preloading with HD optimization
const loadImageAsBase64HD = async (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // HD scaling - use device pixel ratio for better quality
        const scale = Math.max(window.devicePixelRatio || 1, 2);
        canvas.width = img.naturalWidth * scale;
        canvas.height = img.naturalHeight * scale;
        
        if (ctx) {
          // Enable HD rendering settings
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Scale context for HD rendering
          ctx.scale(scale, scale);
          
          // Draw with HD quality
          ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
          
          // Export with high quality
          const base64 = canvas.toDataURL('image/png', 0.98);
          resolve(base64);
        } else {
          reject(new Error('Failed to get canvas context'));
        }
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
};

// Enhanced logo loading with HD processing
const ensureLogoLoadedHD = async (htmlContent: string): Promise<string> => {
  const logoUrl = '/lovable-uploads/53d20b53-cf82-46b3-9fec-7f835cf99643.png';
  
  try {
    console.log('Converting logo to HD base64 for superior print quality...');
    const hdBase64Logo = await loadImageAsBase64HD(logoUrl);
    
    // Replace logo URL with HD base64 in the HTML content
    const updatedContent = htmlContent.replace(
      new RegExp(logoUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
      hdBase64Logo
    );
    
    console.log('Logo successfully converted to HD base64');
    return updatedContent;
  } catch (error) {
    console.warn('HD logo conversion failed, using standard conversion:', error);
    // Fallback to original function
    return htmlContent;
  }
};

export const openPrintWindow = (
  htmlContent: string, 
  title: string, 
  options: PrintOptions = {}
) => {
  try {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Failed to open print window');
    }

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    printWindow.onload = () => {
      if (options.autoPrint) {
        setTimeout(() => {
          printWindow.print();
          if (options.autoClose) {
            setTimeout(() => printWindow.close(), 500);
          }
        }, 500);
      }
    };

    return printWindow;
  } catch (error) {
    if (options.onError) {
      options.onError(error as Error);
    }
    throw error;
  }
};

// ULTRA-HD OPTIMIZED JPG DOWNLOAD WITH FIXED LAYOUT
export const downloadAsJPG = async (
  htmlContent: string,
  title: string,
  options: PrintOptions = {}
): Promise<void> => {
  try {
    console.log('Starting ULTRA-HD JPG download with enhanced quality...');
    
    const html2canvas = (await import('html2canvas')).default;
    const qualityConfig = HD_QUALITY_CONFIG.hd; // Use HD quality by default
    
    // Ensure HD logo is loaded
    const hdContent = await ensureLogoLoadedHD(htmlContent);
    
    // Create HD-optimized iframe with proper A4 dimensions
    const iframe = document.createElement('iframe');
    iframe.style.cssText = `
      position: absolute;
      left: -9999px;
      top: 0;
      width: 794px;
      height: 1123px;
      border: none;
      background: white;
      z-index: -1;
      opacity: 0;
    `;
    
    document.body.appendChild(iframe);
    
    try {
      if (iframe.contentDocument) {
        iframe.contentDocument.open();
        iframe.contentDocument.write(hdContent);
        iframe.contentDocument.close();
        
        // Enhanced loading wait for HD quality
        await new Promise<void>(resolve => {
          const checkReady = () => {
            if (iframe.contentDocument?.readyState === 'complete') {
              setTimeout(resolve, qualityConfig.imageTimeout);
            } else {
              setTimeout(checkReady, 100);
            }
          };
          checkReady();
          setTimeout(resolve, qualityConfig.imageTimeout + 500);
        });

        const targetElement = iframe.contentDocument.body;
        
        if (targetElement) {
          console.log('Converting to HD canvas with superior quality settings...');
          
          const canvas = await html2canvas(targetElement, {
            width: 794,
            height: 1123,
            scale: qualityConfig.scale, // HD scale
            useCORS: false,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            imageTimeout: qualityConfig.imageTimeout,
            removeContainer: true,
            foreignObjectRendering: false,
            // HD Quality specific settings - FIXED LAYOUT
            windowWidth: 794,
            windowHeight: 1123,
            ignoreElements: (element) => {
              return element.tagName === 'SCRIPT' || element.tagName === 'NOSCRIPT';
            },
            onclone: (clonedDoc) => {
              // Ensure all images are visible with HD settings
              const images = clonedDoc.querySelectorAll('img');
              images.forEach(img => {
                img.style.display = 'block';
                img.style.visibility = 'visible';
                img.style.opacity = '1';
                img.style.imageRendering = 'crisp-edges';
              });
              
              // Apply HD text rendering to cloned document
              const style = clonedDoc.createElement('style');
              style.textContent = `
                * {
                  -webkit-font-smoothing: antialiased !important;
                  -moz-osx-font-smoothing: grayscale !important;
                  text-rendering: optimizeLegibility !important;
                  font-smooth: always !important;
                }
              `;
              clonedDoc.head.appendChild(style);
            }
          });

          console.log('HD Canvas created, generating superior quality JPG...');

          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${title.replace(/\s+/g, '_')}_HD_${new Date().toISOString().split('T')[0]}.jpg`;
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              console.log('ULTRA-HD JPG download completed successfully!');
            } else {
              throw new Error('Failed to create HD blob');
            }
          }, 'image/jpeg', qualityConfig.jpegQuality);
        }
      }
    } finally {
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 100);
    }

  } catch (error) {
    console.error('Error downloading HD JPG:', error);
    if (options.onError) {
      options.onError(error as Error);
    }
    throw error;
  }
};

// ULTRA-HD OPTIMIZED PDF DOWNLOAD WITH FIXED LAYOUT
export const downloadAsPDF = async (
  htmlContent: string,
  title: string,
  options: PrintOptions = {}
): Promise<void> => {
  try {
    console.log('Starting ULTRA-HD PDF download with enhanced quality...');
    
    const [jsPDF, html2canvas] = await Promise.all([
      import('jspdf').then(module => module.jsPDF),
      import('html2canvas').then(module => module.default)
    ]);
    
    const qualityConfig = HD_QUALITY_CONFIG.hd; // Use HD quality by default
    
    // Ensure HD logo is loaded
    const hdContent = await ensureLogoLoadedHD(htmlContent);
    
    // Create HD-optimized iframe with proper A4 dimensions
    const iframe = document.createElement('iframe');
    iframe.style.cssText = `
      position: absolute;
      left: -9999px;
      top: 0;
      width: 794px;
      height: 1123px;
      border: none;
      background: white;
      z-index: -1;
      opacity: 0;
    `;
    
    document.body.appendChild(iframe);
    
    try {
      if (iframe.contentDocument) {
        iframe.contentDocument.open();
        iframe.contentDocument.write(hdContent);
        iframe.contentDocument.close();
        
        // Enhanced loading wait for HD quality
        await new Promise<void>(resolve => {
          const checkReady = () => {
            if (iframe.contentDocument?.readyState === 'complete') {
              setTimeout(resolve, qualityConfig.imageTimeout);
            } else {
              setTimeout(checkReady, 100);
            }
          };
          checkReady();
          setTimeout(resolve, qualityConfig.imageTimeout + 500);
        });

        const targetElement = iframe.contentDocument.body;
        
        if (targetElement) {
          console.log('Converting to HD canvas for superior PDF quality...');
          
          const canvas = await html2canvas(targetElement, {
            width: 794,
            height: 1123,
            scale: qualityConfig.scale, // HD scale
            useCORS: false,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            imageTimeout: qualityConfig.imageTimeout,
            removeContainer: true,
            foreignObjectRendering: false,
            // HD Quality specific settings - FIXED LAYOUT
            windowWidth: 794,
            windowHeight: 1123,
            onclone: (clonedDoc) => {
              const images = clonedDoc.querySelectorAll('img');
              images.forEach(img => {
                img.style.display = 'block';
                img.style.visibility = 'visible';
                img.style.opacity = '1';
                img.style.imageRendering = 'crisp-edges';
              });
              
              // Apply HD text rendering
              const style = clonedDoc.createElement('style');
              style.textContent = `
                * {
                  -webkit-font-smoothing: antialiased !important;
                  -moz-osx-font-smoothing: grayscale !important;
                  text-rendering: optimizeLegibility !important;
                  font-smooth: always !important;
                }
              `;
              clonedDoc.head.appendChild(style);
            }
          });

          console.log('HD Canvas created, generating superior quality PDF...');

          const imgData = canvas.toDataURL('image/png', qualityConfig.pngQuality);
          const pdf = new jsPDF('p', 'mm', 'a4');
          
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
          const imgX = (pdfWidth - imgWidth * ratio) / 2;
          const imgY = 0;

          pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio, '', 'FAST');
          
          pdf.save(`${title.replace(/\s+/g, '_')}_HD_${new Date().toISOString().split('T')[0]}.pdf`);
          console.log('ULTRA-HD PDF download completed successfully!');
        }
      }
    } finally {
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 100);
    }

  } catch (error) {
    console.error('Error downloading HD PDF:', error);
    if (options.onError) {
      options.onError(error as Error);
    }
    throw error;
  }
};

export const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const isChrome = /Chrome/.test(userAgent);
  const isFirefox = /Firefox/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  const isEdge = /Edge/.test(userAgent);
  const isNativefier = /Nativefier/.test(userAgent);
  const isDesktopApp = isNativefier || (window as any).electronAPI !== undefined;
  
  return {
    isChrome,
    isFirefox,
    isSafari,
    isEdge,
    isNativefier,
    isDesktopApp,
    userAgent
  };
};

export const printHTML = (htmlContent: string, title: string, options?: { delay?: number }) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Failed to open print window');
  }

  printWindow.document.open();
  printWindow.document.write(htmlContent);
  printWindow.document.close();

  const delay = options?.delay || 1000;
  
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
    }, delay);
  };

  return printWindow;
};

export const openInExternalBrowser = (htmlContent: string, title: string) => {
  const dataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(htmlContent)}`;
  
  try {
    if ((window as any).electronAPI) {
      (window as any).electronAPI.openExternal(dataUrl);
    } else {
      const newWindow = window.open(dataUrl, '_blank');
      if (newWindow) {
        setTimeout(() => {
          newWindow.print();
        }, 1000);
      }
    }
  } catch (error) {
    console.error('Error opening in external browser:', error);
    printHTML(htmlContent, title);
  }
};
